import { Checkbox } from 'antd';
import cn from 'classnames';
import { produce } from 'immer';
import React from 'react';

import { useFiltersQuery } from '@/hooks/used-cars/filters-query.hook';
import { ChildFilter, ParentFilter } from '@/types/filters/filters.types';

import { GroupHeading } from './components/group-heading/group-heading';
import styles from './nested-checkboxes.module.scss';

export const NestedCheckboxes: React.FC<{
  selectFilter: (filterToUpdate: ParentFilter | ChildFilter) => void;
  options: ParentFilter[];
  setOptionsState: (
    updateFn: (prevState: ParentFilter[]) => ParentFilter[],
  ) => void;
  resetText: string;
  selectAllText: string;
  hideText: string;
  allSelected: boolean;
  activeCarType?: string;
  openText?: string;
}> = ({
  selectFilter,
  options,
  setOptionsState,
  resetText,
  selectAllText,
  hideText,
  allSelected,
  activeCarType,
  openText,
}) => {
  const { setIsCleared } = useFiltersQuery();

  function toggleAllSelected() {
    setOptionsState((options: ParentFilter[]) => {
      return produce(options, (draftOptions) => {
        const isAllChecked = draftOptions.every((option) => option.isChecked);

        draftOptions.forEach((option) => {
          option.isChecked = !isAllChecked;

          option.children?.forEach((child) => {
            child.isChecked = !isAllChecked;
          });
        });
      });
    });
  }

  function clearAll() {
    setOptionsState((options: ParentFilter[]) => {
      return produce(options, (draftOptions) => {
        draftOptions.forEach((option) => {
          option.isChecked = false;
          option.isOpened = false;

          option.children?.forEach((child) => {
            child.isChecked = false;
          });
        });
      });
    });

    setIsCleared(true);
  }

  return (
    <div className={styles.nestedCheckboxes}>
      <span className={styles.nestedCheckboxesResetFilters} onClick={clearAll}>
        {resetText}
      </span>
      <div
        className={cn(styles.nestedCheckboxesSelectAll, {
          [styles.nestedCheckboxesSelectAllSelected as string]: allSelected,
        })}
      >
        <Checkbox
          className={styles.nestedCheckboxesSelectAllInput}
          checked={allSelected}
          onChange={toggleAllSelected}
        />
        <span>{selectAllText}</span>
      </div>
      {options.map((option) => (
        <GroupHeading
          key={option.id}
          option={option}
          selectOption={selectFilter}
          activeCarType={activeCarType}
          hideText={hideText}
          setOptionsState={setOptionsState}
          openText={openText}
        />
      ))}
    </div>
  );
};
